import type * as ToneNS from 'tone';
import { DEFAULT_PITCH } from '../domain/factories';
import { audibleInstruments, busAudible } from '../domain/projectOps';
import { INSTRUMENTS, type AudioBus, type Instrument, type SampleSlot, type Track } from '../domain/schemas';
import { PPQ, type ScheduledClip, type ScheduledNote } from '../music/compile';
import { midiToFrequency } from '../music/theory';

export type ToneModule = typeof ToneNS;

/**
 * The instrument + mixer graph, written against the plain Web Audio API so the exact
 * same code runs on a realtime AudioContext (live playback, scheduled by Tone.js) and a
 * native OfflineAudioContext (WAV export).
 *
 *   note voices → [instrument filter] → drive (wave-shaper) → low-pass → volume/mute
 *     → pan ─┬→ master bus
 *            ├→ delay send → feedback delay ─┤
 *            └→ reverb send → convolver ─────┤
 *   master bus → master gain → glue compressor → limiter (−2 dB) → soft clipper → out
 *
 *   audio clips → clip gain (fades) → [clip filter] → audio bus strip (volume/pan/sends)
 *   sample hits → hit gain → the drum track's strip (replacing the synthesized voice)
 *
 * Voices create short-lived source nodes per note (zero cost when silent) and remember
 * them so `releaseAll()` can silence everything instantly (no stuck notes).
 */
export interface Graph {
  /** Plays a note: a sample hit when the track has a sample slot, otherwise the synth. */
  trigger(note: ScheduledNote, time: number): void;
  /**
   * Starts an audio clip at `time`. `fromSec` > 0 starts partway through (playback
   * started or resumed in the middle of the clip).
   */
  playClip(clip: ScheduledClip, time: number, fromSec?: number): void;
  /** Stops every clip voice (sample hits and synth notes keep ringing). */
  stopClips(): void;
  applyMix(tracks: readonly Track[], masterVolumeDb: number, bus?: AudioBus): void;
  setTempo(bpm: number): void;
  /** Stops every sounding or scheduled voice (prevents stuck notes on stop). */
  releaseAll(): void;
  /** Ends a held mono 808 note once no further notes will be scheduled. */
  flush(): void;
  /** Disconnects voices that ended before `time` (keeps long renders lean). */
  collect(time: number): void;
  /** Peak output level in dBFS from the analyser (−Infinity when silent / no meter). */
  getLevel(): number;
  /** Notes that could not be scheduled (expected to stay 0). */
  droppedNotes: number;
  /** Notes scheduled so far. */
  triggered: number;
  /** Clips started so far. */
  clipsStarted: number;
  /** Assets whose audio was not loaded when a hit or clip needed it (never substituted). */
  readonly missingAssets: Set<string>;
  /** Voices (oscillators and buffer sources) currently scheduled or sounding. */
  liveVoices(): number;
  dispose(): void;
}

interface Live {
  stop: number;
  nodes: AudioNode[];
}

const dbToGain = (db: number) => (db <= -60 ? 0 : 10 ** (db / 20));

/** Synchronous access to decoded audio (see AudioBufferCache.peek). */
export interface AudioSource {
  peek(assetId: string, reversed: boolean): AudioBuffer | undefined;
}

export const DEFAULT_BUS: AudioBus = { volumeDb: 0, pan: 0, muted: false, solo: false, delaySend: 0, reverbSend: 0 };

/** Playback rate for a sample hit: slot transpose plus the note's distance from the track's default pitch. */
export function sampleRate(slot: SampleSlot, instrument: Instrument, pitch: number): number {
  return 2 ** ((slot.pitchSemitones + pitch - DEFAULT_PITCH[instrument]) / 12);
}

/** Gain envelope of a clip at `t` seconds into its timeline span (fades are linear). */
export function clipEnvelopeAt(clip: Pick<ScheduledClip, 'fadeInSec' | 'fadeOutSec' | 'playSec'>, t: number, fullSec: number): number {
  let g = 1;
  if (clip.fadeInSec > 0 && t < clip.fadeInSec) g = Math.min(g, Math.max(0, t / clip.fadeInSec));
  const fadeOutStart = fullSec - clip.fadeOutSec;
  if (clip.fadeOutSec > 0 && t > fadeOutStart) g = Math.min(g, Math.max(0, (fullSec - t) / clip.fadeOutSec));
  return g;
}

/** Short ramp used to avoid clicks when a hit is choked or a clip is cut off. */
const DECLICK = 0.004;

/** Deterministic noise so renders are reproducible bit for bit. */
function seededNoise(length: number, seed: number): Float32Array {
  const data = new Float32Array(length);
  let s = seed >>> 0;
  for (let i = 0; i < length; i++) {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    data[i] = s / 2147483648 - 1;
  }
  return data;
}

function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  buffer.getChannelData(0).set(seededNoise(ctx.sampleRate, 0x2f6b9c1d));
  return buffer;
}

/** Synthetic stereo room impulse: decaying noise, slightly decorrelated per channel. */
function impulseResponse(ctx: BaseAudioContext, seconds: number, decay: number): AudioBuffer {
  const length = Math.round(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const data = buffer.getChannelData(c);
    data.set(seededNoise(length, 0x51ed270b + c * 977));
    for (let i = 0; i < length; i++) data[i]! *= (1 - i / length) ** decay;
  }
  return buffer;
}

/** Drive curve: smooth saturation whose strength grows with `amount` (0–1). */
export function driveCurve(amount: number, size = 2048): Float32Array {
  const k = amount * 50;
  const curve = new Float32Array(size);
  for (let i = 0; i < size; i++) {
    const x = (i * 2) / (size - 1) - 1;
    curve[i] = ((1 + k) * x) / (1 + k * Math.abs(x));
  }
  return curve;
}

/** Transparent below 0.9, then gently saturating so the output can never exceed ±1. */
export function softClipCurve(size = 4096): Float32Array {
  const curve = new Float32Array(size);
  const knee = 0.9;
  for (let i = 0; i < size; i++) {
    const x = (i * 2) / (size - 1) - 1;
    const a = Math.abs(x);
    const y = a <= knee ? a : knee + (1 - knee) * Math.tanh((a - knee) / (1 - knee));
    curve[i] = Math.sign(x) * Math.min(0.999, y);
  }
  return curve;
}

/** Percussive envelope: fast attack then exponential decay. Returns the stop time. */
function percEnv(gain: AudioParam, t: number, peak: number, attack: number, decay: number): number {
  gain.setValueAtTime(0, t);
  gain.linearRampToValueAtTime(peak, t + attack);
  gain.setTargetAtTime(0, t + attack, decay / 4);
  return t + attack + decay * 1.6;
}

interface Env {
  attack: number;
  decay: number;
  sustain: number;
  release: number;
}

/** Sustained envelope; the release starts after `hold`. Returns the stop time. */
function sustainEnv(gain: AudioParam, t: number, peak: number, hold: number, e: Env): number {
  gain.setValueAtTime(0, t);
  gain.linearRampToValueAtTime(peak, t + e.attack);
  gain.setTargetAtTime(peak * e.sustain, t + e.attack, e.decay / 3);
  const releaseAt = t + Math.max(hold, e.attack);
  gain.setTargetAtTime(0, releaseAt, e.release / 4);
  return releaseAt + e.release * 1.6;
}

const delayTimeFor = (bpm: number) => (60 / bpm) * 0.75; // dotted eighth

type Play = (note: ScheduledNote, t: number, seconds: number) => void;

export function buildGraph(
  ctx: BaseAudioContext,
  destination: AudioNode,
  tracks: readonly Track[],
  masterVolumeDb: number,
  bpm: number,
  options: { meter: boolean; audio?: AudioSource; bus?: AudioBus },
): Graph {
  const persistent: AudioNode[] = [];
  const keep = <T extends AudioNode>(node: T): T => {
    persistent.push(node);
    return node;
  };
  const live = new Map<AudioScheduledSourceNode, Live>();
  const noise = noiseBuffer(ctx);

  // ---- Master bus
  const master = keep(ctx.createGain());
  master.gain.value = dbToGain(masterVolumeDb);
  const glue = keep(ctx.createDynamicsCompressor());
  glue.threshold.value = -16;
  glue.knee.value = 6;
  glue.ratio.value = 2.5;
  glue.attack.value = 0.01;
  glue.release.value = 0.2;
  const limiter = keep(ctx.createDynamicsCompressor());
  limiter.threshold.value = -2;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.001;
  limiter.release.value = 0.08;
  const clipper = keep(ctx.createWaveShaper());
  clipper.curve = softClipCurve() as Float32Array<ArrayBuffer>;
  master.connect(glue);
  glue.connect(limiter);
  limiter.connect(clipper);
  clipper.connect(destination);
  const analyser = options.meter ? keep(ctx.createAnalyser()) : null;
  if (analyser) {
    analyser.fftSize = 1024;
    clipper.connect(analyser);
  }
  const meterData = analyser ? new Float32Array(analyser.fftSize) : null;

  // ---- Shared effect buses
  const reverb = keep(ctx.createConvolver());
  reverb.buffer = impulseResponse(ctx, 2.4, 3);
  const reverbOut = keep(ctx.createGain());
  reverbOut.gain.value = 0.6;
  reverb.connect(reverbOut);
  reverbOut.connect(master);
  const delay = keep(ctx.createDelay(2));
  delay.delayTime.value = delayTimeFor(bpm);
  const feedback = keep(ctx.createGain());
  feedback.gain.value = 0.32;
  const damping = keep(ctx.createBiquadFilter());
  damping.type = 'lowpass';
  damping.frequency.value = 4500;
  delay.connect(damping);
  damping.connect(feedback);
  feedback.connect(delay);
  damping.connect(master);

  // ---- Channel strips
  interface Strip {
    input: GainNode;
    shaper: WaveShaperNode;
    filter: BiquadFilterNode;
    volume: GainNode;
    pan: StereoPannerNode;
    delaySend: GainNode;
    reverbSend: GainNode;
    drive: number;
  }
  const strips = new Map<Instrument, Strip>();
  for (const instrument of INSTRUMENTS) {
    const input = keep(ctx.createGain());
    const shaper = keep(ctx.createWaveShaper());
    if (instrument === 'bass808') shaper.oversample = '2x';
    const filter = keep(ctx.createBiquadFilter());
    filter.type = 'lowpass';
    filter.Q.value = 0.7;
    filter.frequency.value = 20000;
    const volume = keep(ctx.createGain());
    const pan = keep(ctx.createStereoPanner());
    const delaySend = keep(ctx.createGain());
    const reverbSend = keep(ctx.createGain());
    delaySend.gain.value = 0;
    reverbSend.gain.value = 0;
    input.connect(shaper);
    shaper.connect(filter);
    filter.connect(volume);
    volume.connect(pan);
    pan.connect(master);
    pan.connect(delaySend);
    pan.connect(reverbSend);
    delaySend.connect(delay);
    reverbSend.connect(reverb);
    strips.set(instrument, { input, shaper, filter, volume, pan, delaySend, reverbSend, drive: -1 });
  }

  // ---- Audio clip bus
  const busIn = keep(ctx.createGain());
  const busVolume = keep(ctx.createGain());
  const busPan = keep(ctx.createStereoPanner());
  const busDelay = keep(ctx.createGain());
  const busReverb = keep(ctx.createGain());
  busDelay.gain.value = 0;
  busReverb.gain.value = 0;
  busIn.connect(busVolume);
  busVolume.connect(busPan);
  busPan.connect(master);
  busPan.connect(busDelay);
  busPan.connect(busReverb);
  busDelay.connect(delay);
  busReverb.connect(reverb);

  // ---- Voice helpers
  const register = (source: AudioScheduledSourceNode, start: number, stop: number, nodes: AudioNode[], offset?: number) => {
    live.set(source, { stop, nodes });
    source.onended = () => {
      live.delete(source);
      source.disconnect();
      for (const n of nodes) n.disconnect();
    };
    if (offset !== undefined) (source as AudioBufferSourceNode).start(start, offset);
    else source.start(start);
    source.stop(stop);
  };
  const envGain = (out: AudioNode) => {
    const g = ctx.createGain();
    g.gain.value = 0;
    g.connect(out);
    return g;
  };
  const osc = (type: OscillatorType, freq: number, t: number, stop: number, out: GainNode, detune = 0, sweepFrom?: number, sweep = 0.05) => {
    const o = ctx.createOscillator();
    o.type = type;
    o.detune.value = detune;
    if (sweepFrom) {
      o.frequency.setValueAtTime(sweepFrom, t);
      o.frequency.exponentialRampToValueAtTime(freq, t + sweep);
    } else {
      o.frequency.setValueAtTime(freq, t);
    }
    o.connect(out);
    register(o, t, stop, [out]);
    return o;
  };
  const noiseHit = (t: number, stop: number, out: GainNode) => {
    const src = ctx.createBufferSource();
    src.buffer = noise;
    src.loop = true;
    src.connect(out);
    // Deterministic, time-dependent offset so consecutive hits use different noise.
    register(src, t, stop, [out], (t * 7.31) % 0.9);
  };
  const filterInto = (input: AudioNode, type: BiquadFilterType, frequency: number, q = 0.7, gainDb = 0) => {
    const f = keep(ctx.createBiquadFilter());
    f.type = type;
    f.frequency.value = frequency;
    f.Q.value = q;
    f.gain.value = gainDb;
    f.connect(input);
    return f;
  };

  // 808 is monophonic with glide: a note that starts while the previous one still
  // sounds slides from its pitch, and the previous note is faded out.
  let held808: { osc: OscillatorNode; gain: GainNode; freq: number; end: number } | null = null;
  const stop808 = (at: number) => {
    if (!held808) return;
    const { osc: o, gain, end } = held808;
    const stopAt = Math.min(end, at + 0.03);
    gain.gain.setTargetAtTime(0, Math.min(end, at), 0.006);
    o.stop(stopAt);
    live.set(o, { stop: stopAt, nodes: [gain] });
    held808 = null;
  };

  const voices: Record<Instrument, Play> = {
    kick: (n, t, d) => {
      const out = envGain(strips.get('kick')!.input);
      const f = midiToFrequency(n.pitch);
      osc('sine', f, t, percEnv(out.gain, t, n.velocity * 0.8, 0.001, Math.min(0.5, Math.max(0.25, d))), out, 0, f * 8, 0.05);
    },
    perc: (n, t) => {
      const out = envGain(strips.get('perc')!.input);
      const f = midiToFrequency(n.pitch);
      osc('sine', f, t, percEnv(out.gain, t, n.velocity * 1.6, 0.001, 0.2), out, 0, f * 4, 0.02);
    },
    snare: (() => {
      const hp = filterInto(strips.get('snare')!.input, 'highpass', 900);
      return (n: ScheduledNote, t: number) => {
        const g = envGain(hp);
        noiseHit(t, percEnv(g.gain, t, n.velocity * 3.5, 0.001, 0.18), g);
        const body = envGain(strips.get('snare')!.input);
        const f = midiToFrequency(n.pitch + 12);
        osc('triangle', f, t, percEnv(body.gain, t, n.velocity * 1.5, 0.001, 0.1), body, 0, f * 2, 0.03);
      };
    })(),
    clap: (() => {
      const band = filterInto(strips.get('clap')!.input, 'bandpass', 1300, 0.9);
      return (n: ScheduledNote, t: number) => {
        // Three rapid bursts then a tail: the classic hand-clap envelope.
        const g = envGain(band);
        g.gain.setValueAtTime(0, t);
        [0, 0.011, 0.022].forEach((at, k) => {
          g.gain.setValueAtTime(n.velocity * (k === 2 ? 10 : 8), t + at);
          g.gain.setTargetAtTime(0, t + at + 0.001, k === 2 ? 0.045 : 0.004);
        });
        noiseHit(t, t + 0.25, g);
      };
    })(),
    hats: (() => {
      // Filtered white noise: the classic drum-machine hi-hat (open when the note is long).
      const hp = filterInto(strips.get('hats')!.input, 'highpass', 7000, 0.9);
      const presence = filterInto(hp, 'peaking', 10000, 1, 4);
      return (n: ScheduledNote, t: number, d: number) => {
        const g = envGain(presence);
        noiseHit(t, percEnv(g.gain, t, n.velocity * 3, 0.001, d >= 0.18 ? 0.28 : 0.05), g);
      };
    })(),
    bass808: (n, t, d) => {
      const out = envGain(strips.get('bass808')!.input);
      const f = midiToFrequency(n.pitch);
      const glideFrom = held808 && held808.end > t ? held808.freq : undefined;
      stop808(t);
      const end = sustainEnv(out.gain, t, n.velocity * 0.45, Math.max(0.05, d), { attack: 0.004, decay: 0.4, sustain: 0.75, release: 0.18 });
      const o = ctx.createOscillator();
      o.type = 'sine';
      if (glideFrom) {
        o.frequency.setValueAtTime(glideFrom, t);
        o.frequency.exponentialRampToValueAtTime(f, t + 0.035);
      } else {
        o.frequency.setValueAtTime(f, t);
      }
      o.connect(out);
      live.set(o, { stop: end, nodes: [out] });
      o.onended = () => {
        live.delete(o);
        o.disconnect();
        out.disconnect();
      };
      o.start(t);
      held808 = { osc: o, gain: out, freq: f, end };
    },
    cowbell: (() => {
      // Two detuned square oscillators through a band-pass: the 808 cowbell recipe.
      const presence = filterInto(strips.get('cowbell')!.input, 'peaking', 2600, 1.2, 6);
      const band = filterInto(presence, 'highpass', 350, 0.7);
      return (n: ScheduledNote, t: number, d: number) => {
        const g = envGain(band);
        const f = midiToFrequency(n.pitch);
        const stop = sustainEnv(g.gain, t, n.velocity, Math.min(0.16, Math.max(0.04, d)), { attack: 0.001, decay: 0.2, sustain: 0.05, release: 0.06 });
        osc('square', f, t, stop, g);
        osc('square', f * 1.4836, t, stop, g);
      };
    })(),
    lead: (n, t, d) => {
      const g = envGain(strips.get('lead')!.input);
      const f = midiToFrequency(n.pitch);
      const stop = sustainEnv(g.gain, t, n.velocity, Math.max(0.05, d), { attack: 0.006, decay: 0.25, sustain: 0.45, release: 0.25 });
      osc('sawtooth', f, t, stop, g, -7);
      osc('sawtooth', f, t, stop, g, 7);
    },
    pad: (n, t, d) => {
      const g = envGain(strips.get('pad')!.input);
      const f = midiToFrequency(n.pitch);
      const stop = sustainEnv(g.gain, t, n.velocity * 1.2, Math.max(0.1, d), { attack: 0.5, decay: 0.6, sustain: 0.7, release: 1.2 });
      osc('sawtooth', f, t, stop, g, -12);
      osc('sawtooth', f, t, stop, g, 12);
    },
  };

  // ---- Sample instruments
  let slots = new Map<Instrument, SampleSlot>();
  const setSlots = (nextTracks: readonly Track[]) => {
    slots = new Map(nextTracks.flatMap((t) => (t.sample ? [[t.instrument, t.sample] as const] : [])));
  };
  const missingAssets = new Set<string>();
  const needBuffer = (assetId: string, reversed: boolean): AudioBuffer => {
    const buffer = options.audio?.peek(assetId, reversed);
    if (!buffer) {
      missingAssets.add(assetId);
      throw new Error(`audio for asset ${assetId} is not loaded`);
    }
    return buffer;
  };
  // Choke groups: a new hit fades out the previous hit of the same group.
  const chokes = new Map<number, { source: AudioBufferSourceNode; gain: GainNode; end: number }>();
  const playSample = (slot: SampleSlot, n: ScheduledNote, t: number) => {
    const buffer = needBuffer(slot.assetId, slot.reverse);
    const rate = sampleRate(slot, n.instrument, n.pitch);
    const length = buffer.duration;
    const start = Math.min(slot.trimStartSec, length);
    const end = Math.min(slot.trimEndSec ?? length, length);
    if (end - start <= 0.0005) return;
    // In a reversed buffer the trimmed region [start, end] sits at [length − end, length − start].
    const offset = slot.reverse ? length - end : start;
    const playSec = (end - start) / rate;
    if (slot.chokeGroup > 0) {
      const previous = chokes.get(slot.chokeGroup);
      if (previous && previous.end > t) {
        previous.gain.gain.setTargetAtTime(0, t, DECLICK / 3);
        try {
          previous.source.stop(t + DECLICK * 2);
        } catch {
          // Already stopped.
        }
      }
    }
    const gain = ctx.createGain();
    const level = n.velocity * dbToGain(slot.gainDb);
    gain.gain.setValueAtTime(level, t);
    // Tiny release at the trim end so cut samples don't click.
    gain.gain.setValueAtTime(level, Math.max(t, t + playSec - DECLICK));
    gain.gain.linearRampToValueAtTime(0, t + playSec);
    gain.connect(strips.get(n.instrument)!.input);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = rate;
    src.connect(gain);
    register(src, t, t + playSec + 0.001, [gain], offset);
    if (slot.chokeGroup > 0) chokes.set(slot.chokeGroup, { source: src, gain, end: t + playSec });
  };

  // ---- Audio clips
  const clipSources = new Set<AudioScheduledSourceNode>();
  const playClip = (clip: ScheduledClip, time: number, fromSec: number) => {
    if (fromSec >= clip.playSec - 0.001) return;
    const buffer = needBuffer(clip.assetId, clip.reverse);
    const passSec = clip.durationSec / clip.playbackRate;
    const fullSec = passSec * clip.loops;
    const endSec = clip.playSec;
    const out = ctx.createGain();
    // Envelope over the clip's timeline span, starting wherever playback joins it.
    const g = out.gain;
    const at = (sec: number) => time + (sec - fromSec);
    g.setValueAtTime(clip.gain * clipEnvelopeAt(clip, fromSec, fullSec), time);
    if (clip.fadeInSec > fromSec) g.linearRampToValueAtTime(clip.gain, at(Math.min(clip.fadeInSec, endSec)));
    const fadeOutStart = fullSec - clip.fadeOutSec;
    if (clip.fadeOutSec > 0 && fadeOutStart < endSec) {
      if (fadeOutStart > fromSec) g.setValueAtTime(clip.gain * clipEnvelopeAt(clip, fadeOutStart, fullSec), at(fadeOutStart));
      g.linearRampToValueAtTime(clip.gain * clipEnvelopeAt(clip, endSec, fullSec), at(endSec));
    }
    if (endSec < fullSec - 0.001) {
      // Cut off by the end of the song: short release instead of a click.
      g.setValueAtTime(clip.gain * clipEnvelopeAt(clip, endSec - DECLICK, fullSec), at(Math.max(fromSec, endSec - DECLICK)));
      g.linearRampToValueAtTime(0, at(endSec));
    }
    let tail: AudioNode = out;
    const nodes: AudioNode[] = [out];
    if (clip.filter.type !== 'off') {
      const f = ctx.createBiquadFilter();
      f.type = clip.filter.type;
      f.frequency.value = clip.filter.frequencyHz;
      f.Q.value = 0.7;
      out.connect(f);
      tail = f;
      nodes.push(f);
    }
    tail.connect(busIn);
    // Region of the (possibly reversed) buffer that one pass plays.
    const regionStart = clip.reverse ? buffer.duration - clip.offsetSec - clip.durationSec : clip.offsetSec;
    let started = 0;
    for (let i = 0; i < clip.loops; i++) {
      const passStart = i * passSec;
      const passEnd = Math.min(passStart + passSec, endSec);
      if (passEnd <= fromSec || passStart >= endSec) continue;
      const join = Math.max(passStart, fromSec);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = clip.playbackRate;
      src.connect(out);
      const offset = Math.max(0, regionStart + (join - passStart) * clip.playbackRate);
      // The last source owns the shared gain/filter nodes and disconnects them.
      clipSources.add(src);
      register(src, at(join), at(passEnd), [], offset);
      const onended = src.onended;
      src.onended = (event) => {
        clipSources.delete(src);
        onended?.call(src, event);
        if (--started === 0) {
          liveExtras.delete(out);
          for (const n of nodes) n.disconnect();
        }
      };
      started++;
    }
    if (started === 0) for (const n of nodes) n.disconnect();
    else liveExtras.set(out, { nodes, end: at(endSec) });
  };
  // Shared clip nodes, so releaseAll/collect can disconnect them even if a source never fires onended.
  const liveExtras = new Map<AudioNode, { nodes: AudioNode[]; end: number }>();

  let tempo = bpm;
  let initialising = true;
  const smooth = (param: AudioParam, value: number) => {
    if (initialising) {
      // The initial mix must be exact from sample 0 (e.g. a muted track must not leak).
      param.value = value;
      return;
    }
    // Short ramp avoids zipper noise on live changes.
    param.setTargetAtTime(value, ctx.currentTime, 0.012);
  };

  const graph: Graph = {
    droppedNotes: 0,
    triggered: 0,
    clipsStarted: 0,
    missingAssets,
    liveVoices: () => live.size,
    trigger(note, time) {
      const seconds = note.durationTicks * (60 / (tempo * PPQ));
      try {
        const slot = slots.get(note.instrument);
        // A track with a sample never falls back to its synth voice: a missing sample is silence + an error.
        if (slot) playSample(slot, note, time);
        else voices[note.instrument](note, time, seconds);
        graph.triggered++;
      } catch (error) {
        // Never let one rejected note stop the whole transport; count it so tests catch it.
        graph.droppedNotes++;
        if (graph.droppedNotes <= 3) console.warn(`SonicForge: dropped a ${note.instrument} note`, error);
      }
    },
    playClip(clip, time, fromSec = 0) {
      try {
        playClip(clip, time, fromSec);
        graph.clipsStarted++;
      } catch (error) {
        graph.droppedNotes++;
        if (graph.droppedNotes <= 3) console.warn('SonicForge: could not play a clip', error);
      }
    },
    stopClips() {
      for (const source of clipSources) {
        try {
          source.stop();
        } catch {
          // Not started yet or already stopped.
        }
        source.disconnect();
        live.delete(source);
      }
      clipSources.clear();
      for (const { nodes } of liveExtras.values()) for (const n of nodes) n.disconnect();
      liveExtras.clear();
    },
    applyMix(nextTracks, nextMasterDb, bus = DEFAULT_BUS) {
      smooth(master.gain, dbToGain(nextMasterDb));
      setSlots(nextTracks);
      const audible = audibleInstruments(nextTracks, bus);
      smooth(busVolume.gain, busAudible(nextTracks, bus) ? dbToGain(bus.volumeDb) : 0);
      smooth(busPan.pan, bus.pan);
      smooth(busDelay.gain, bus.delaySend);
      smooth(busReverb.gain, bus.reverbSend);
      for (const [instrument, strip] of strips) {
        const t = nextTracks.find((x) => x.instrument === instrument);
        smooth(strip.volume.gain, t && audible.has(instrument) ? dbToGain(t.volumeDb) : 0);
        if (!t) continue;
        smooth(strip.pan.pan, t.pan);
        smooth(strip.filter.frequency, t.effects.cutoffHz);
        smooth(strip.delaySend.gain, t.effects.delaySend);
        smooth(strip.reverbSend.gain, t.effects.reverbSend);
        if (t.effects.drive !== strip.drive) {
          strip.drive = t.effects.drive;
          // No curve = bit-transparent pass-through.
          strip.shaper.curve = t.effects.drive > 0 ? (driveCurve(t.effects.drive) as Float32Array<ArrayBuffer>) : null;
        }
      }
    },
    setTempo(nextBpm) {
      tempo = nextBpm;
      smooth(delay.delayTime, delayTimeFor(nextBpm));
    },
    releaseAll() {
      held808 = null;
      chokes.clear();
      graph.stopClips();
      for (const [source, entry] of live) {
        try {
          source.stop();
        } catch {
          // Not started yet or already stopped.
        }
        source.disconnect();
        for (const n of entry.nodes) n.disconnect();
      }
      live.clear();
    },
    flush() {
      if (held808) {
        const { osc: o, end } = held808;
        o.stop(end);
        held808 = null;
      }
    },
    collect(time) {
      for (const [source, entry] of live) {
        if (entry.stop < time - 0.05 && source !== held808?.osc) {
          source.disconnect();
          for (const n of entry.nodes) n.disconnect();
          live.delete(source);
          clipSources.delete(source as AudioBufferSourceNode);
        }
      }
      for (const [out, entry] of liveExtras) {
        if (entry.end < time - 0.05) {
          for (const n of entry.nodes) n.disconnect();
          liveExtras.delete(out);
        }
      }
    },
    getLevel() {
      if (!analyser || !meterData) return -Infinity;
      analyser.getFloatTimeDomainData(meterData);
      let peak = 0;
      for (const v of meterData) peak = Math.max(peak, Math.abs(v));
      return peak > 0 ? 20 * Math.log10(peak) : -Infinity;
    },
    dispose() {
      graph.releaseAll();
      for (const node of persistent) node.disconnect();
    },
  };
  graph.applyMix(tracks, masterVolumeDb, options.bus);
  initialising = false;
  return graph;
}

/** One item of the realtime Part: a note or an audio clip, both positioned in ticks. */
export type PartItem = ({ kind: 'note' } & ScheduledNote) | ({ kind: 'clip' } & ScheduledClip);

/**
 * Schedules a compiled timeline on the Tone transport with a single Tone.Part
 * (realtime playback). Offline export schedules the same events just in time instead.
 */
export function scheduleTimeline(
  tone: ToneModule,
  graph: Graph,
  events: readonly ScheduledNote[],
  clips: readonly ScheduledClip[] = [],
): ToneNS.Part<PartItem & { time: string }> {
  // Tone.Part accepts objects with a `time` key; ticks ("…i") follow the transport tempo.
  const items: (PartItem & { time: string })[] = [
    ...events.map((e) => ({ ...e, kind: 'note' as const, time: `${e.tick}i` })),
    ...clips.map((c) => ({ ...c, kind: 'clip' as const, time: `${c.tick}i` })),
  ];
  const part = new tone.Part<PartItem & { time: string }>((time, item) => {
    const { kind, time: _time, ...event } = item;
    if (kind === 'clip') graph.playClip(event as ScheduledClip, time);
    else graph.trigger(event as ScheduledNote, time);
  }, items);
  part.start(0);
  return part;
}
